import {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { html } from 'lit/static-html.js';
import { ColumnDef } from '@tanstack/react-table';
import {
  langs,
  PopoverWC,
  Spacer,
  TextCaption,
  TextTitle,
  Stack,
  Tooltip,
  Icon,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { throwErrorWithId } from '@pypestream/utils';

import { WithLoadingState } from '../router';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { ActionFile, ActionSection, useEditorModal } from '../components';
import { RowSelectionChangeProps } from '../components/action-section/action-section';
import { StudioPageProps } from '../hooks/use-pro-studio-routes';
import {
  uploadFile,
  blobToFile,
  createFileBlob,
  DataFileType,
  progressServerConfig,
  transformActionFileData,
  isServiceScriptFile,
} from '../utils';
import { useBobcatFlag } from '../hooks/use-bobcat-flag';
import { resolveEditorContent } from './action-editor-content';
import { useSecretHiddenNotice } from './use-secret-hidden-notice';
import { ProStudioContext } from '../xstate/prostudio.xstate.types';
import { useProStudioViewerRole } from '../hooks';
import { tableStore } from '../stores/table-store';
import { withStageNavRedirect } from './redirect-into-stage-nav';

type ActionsPageProps = StudioPageProps & {
  /**
   * Inside Build's Actions panel: no page title of its own, the scripts
   * first (as "Custom", the panel's word for the solution's own commands),
   * and `betweenSections` after them.
   */
  embedded?: boolean;
  /** A line under a script's name — where the panel says which nodes use it. */
  scriptNote?: (fileName: string) => string | undefined;
  /** Drag a script's row onto the canvas or table as an action node. */
  onScriptDragStart?: (fileName: string, e: DragEvent) => void;
  onScriptDragEnd?: () => void;
  betweenSections?: ReactNode;
  /** Build's Actions panel search: only files whose name contains it. */
  query?: string;
};

const ActionsPage: FC<ActionsPageProps> = ({
  embedded,
  scriptNote,
  onScriptDragStart,
  onScriptDragEnd,
  betweenSections,
  query,
}) => {
  const [t] = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const isViewer = useProStudioViewerRole();

  const { functions, configurations, versionId, projectId } =
    useProStudioCtxSelector(
      (ctx) => ({
        projectId: ctx.project?.id,
        versionId:
          ctx.customerId && ctx.botName && ctx.draftVersion
            ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
            : undefined,
        configurations:
          ctx.draft?.files
            .filter((file) => /^[^/]+\.py$/.test(file))
            .map(transformActionFileData) || [],
        functions:
          ctx.draft?.files
            .filter(isServiceScriptFile)
            .map(transformActionFileData) || [],
      }),
      { isEqualityDeep: true }
    );

  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<
    | {
        fileName: string;
        fileData: string;
        type: Omit<DataFileType, 'assetData'>;
      }
    | undefined
  >();

  const pythonLangs = useMemo(() => [langs.python()], []);

  const handleSaveScript = (editorCode: string) => {
    const { fileName, type } = fileData || {};

    if (!projectId) {
      throwErrorWithId('Project ID is required');
    }

    if (!fileName) {
      throwErrorWithId('File name is required');
      return;
    }

    if (!versionId) {
      throwErrorWithId('Version ID is required');
    }

    if (!type) {
      throwErrorWithId('Type is required');
    }

    setIsLoading(true);

    uploadFile({
      url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/${type === 'scriptFile' ? 'scripts' : 'config'}`,
      file: blobToFile(createFileBlob(editorCode, fileName), fileName),
      type: type as DataFileType,
      onChange: ({ data }) => {
        sendProStudioEvent({
          type: 'updateDraftData',
          draft: data as ProStudioContext['draft'],
          action: 'update',
        });

        tableStore.markDraftFilesChanged();
        setIsLoading(false);
      },
      progress: (c, l, total) =>
        progressServerConfig(c, l, total, (p) => setProgress(p)),
    });
  };

  // A private key is never handed to the editor; see resolveEditorContent.
  // Memoised on its three inputs so the credential scan of the file text runs
  // when one of them changes, not on every render of the page.
  // Only with Bobcat's master flag on; off, the editor is candidate's (DoD #2).
  const secretHiddenNotice = useSecretHiddenNotice();
  const withholdKeys = useBobcatFlag('fiveStageNav') === 'allowed';
  const fileText = fileData?.fileData;
  const editorContent = useMemo(
    () =>
      resolveEditorContent(fileText, secretHiddenNotice, {
        isViewer,
        withholdKeys,
      }),
    [fileText, secretHiddenNotice, isViewer, withholdKeys]
  );

  const { editorModal, openModal: openEditorModal } = useEditorModal({
    loading: isLoading,
    progress,
    code: editorContent.code,
    title: fileData?.fileName,
    langs: pythonLangs,
    notice: editorContent.notice,
    viewOnly: editorContent.viewOnly,
    onSave: handleSaveScript,
    onClose: () => {
      setFileData(undefined);
      setProgress(undefined);
      setIsLoading(false);
    },
  });

  const handleOpenEditor = useCallback(
    ({ name, path, type }: RowSelectionChangeProps) => {
      sendProStudioEvent({
        type: 'editScript',
        path,
        name,
        cb: (data) => {
          setFileData({
            fileData: data,
            fileName: name,
            type,
          });
          openEditorModal();
        },
      });
    },
    [openEditorModal]
  );

  const configurationsColumns: ColumnDef<ActionFile>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: () =>
          embedded
            ? ''
            : tRef.current('pro-studio/actions:section1.label', {
                defaultValue: 'File Name',
              }),
        cell: ({ row }) =>
          html`<ps-text-subtitle size="small">
            ${row?.original?.name}
          </ps-text-subtitle>`,
      },
      {
        id: 'actions',
        header: () =>
          html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
        cell: ({ row }) => {
          return html`<ps-stack
            alignItems="center"
            justifyContent="end"
            gutter="xsmall"
            nowrap
          >
            <ps-icon-button
              ?disabled=${row.original.loading || !versionId}
              name="edit"
              size="small"
              @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleOpenEditor({
                  name: row.original.name,
                  path: row.original.path,
                  type: 'configFile',
                });
              }}
            ></ps-icon-button>
            <ps-icon-button
              ?disabled=${row.original.loading || !versionId}
              name="download"
              size="small"
              @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                sendProStudioEvent({
                  type: 'downloadScript',
                  path: row.original.path,
                  name: row.original.name,
                });
              }}
            ></ps-icon-button>
          </ps-stack>`;
        },
        meta: {
          align: 'end',
        },
      },
    ],
    [versionId, handleOpenEditor, embedded]
  );

  const functionsColumns: ColumnDef<ActionFile>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        // In the panel the section heading says what these are.
        header: () =>
          embedded
            ? ''
            : tRef.current('pro-studio/actions:section2.label', {
                defaultValue: 'File Name',
              }),
        cell: ({ row }) => {
          const name = row?.original?.name ?? '';
          const note = scriptNote?.(name);
          const label = html`<ps-text-subtitle size="small">
              ${name}
            </ps-text-subtitle>
            ${note
              ? html`<ps-text-caption variant="tertiary"
                  >${note}</ps-text-caption
                >`
              : ''}`;
          if (!onScriptDragStart || !name.endsWith('.py') || isViewer) {
            return label;
          }
          // Inline styles: the table draws its cells where the panel's
          // stylesheet can't reach.
          return html`<div
            draggable="true"
            title="Drag onto the canvas to add an action node"
            style="display:flex;align-items:center;gap:6px;cursor:grab"
            @dragstart=${(e: DragEvent) => onScriptDragStart(name, e)}
            @dragend=${() => onScriptDragEnd?.()}
          >
            <ps-icon
              name="drag-indicator"
              size="xsmall"
              style="flex:none;color:#aeb2bd"
            ></ps-icon>
            <div style="display:flex;flex-direction:column;min-width:0">
              ${label}
            </div>
          </div>`;
        },
      },
      {
        id: 'actions',
        header: () =>
          html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
        cell: ({ row }) => {
          const popoverId = `delete-popover-${row.original.name}`;
          const popoverTriggerId = `${popoverId}-trigger`;

          const closeDeletePopover = () => {
            const popover = document.getElementById(popoverId) as PopoverWC;
            if (popover) {
              popover.hide();
            }
          };

          return html`<ps-stack
            alignItems="center"
            justifyContent="end"
            gutter="xsmall"
            nowrap
          >
            <ps-icon-button
              ?disabled=${row.original.loading || !versionId}
              name="edit"
              size="small"
              @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleOpenEditor({
                  name: row.original.name,
                  path: row.original.path,
                  type: 'scriptFile',
                });
              }}
            ></ps-icon-button>
            <ps-icon-button
              ?disabled=${row.original.loading || !versionId}
              name="download"
              size="small"
              @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                sendProStudioEvent({
                  type: 'downloadScript',
                  path: row.original.path,
                  name: row.original.name,
                });
              }}
            ></ps-icon-button>
            ${!isViewer
              ? html`
                  <ps-icon-button
                    ?disabled=${row.original.loading || !versionId}
                    id=${popoverTriggerId}
                    name="delete"
                    size="small"
                    @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                    }}
                  ></ps-icon-button>
                  <ps-popover
                    id=${popoverId}
                    popover-id=${popoverId}
                    width="auto"
                    trigger=${popoverTriggerId}
                  >
                    <ps-text-overline variant="tertiary">
                      ${tRef.current(
                        'pro-studio/actions:deleteFilePopover.title',
                        {
                          defaultValue:
                            'Are you sure you want to delete this script?',
                        }
                      )}
                    </ps-text-overline>
                    <ps-spacer size="small"></ps-spacer>
                    <ps-stack
                      direction="row"
                      justifyContent="space-between"
                      nowrap
                    >
                      <ps-button
                        variant="secondary"
                        size="small"
                        width="full"
                        @click=${closeDeletePopover}
                      >
                        ${tRef.current(
                          'pro-studio/common:deleteFilePopover.cancel',
                          {
                            defaultValue: 'Cancel',
                          }
                        )}
                      </ps-button>
                      <ps-button
                        variant="danger"
                        size="small"
                        width="full"
                        @click=${() => {
                          sendProStudioEvent({
                            type: 'deleteScript',
                            id: row.original.name,
                          });
                          closeDeletePopover();
                        }}
                      >
                        ${tRef.current(
                          'pro-studio/common:deleteFilePopover.delete',
                          {
                            defaultValue: 'Delete',
                          }
                        )}
                      </ps-button>
                    </ps-stack>
                  </ps-popover>
                `
              : null}
          </ps-stack>`;
        },
        meta: {
          align: 'end',
        },
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [
      versionId,
      isViewer,
      handleOpenEditor,
      scriptNote,
      onScriptDragStart,
      onScriptDragEnd,
      embedded,
    ]
  );

  const needle = query?.trim().toLowerCase() ?? '';
  const byName = <T extends { name?: string }>(list: T[]) =>
    needle
      ? list.filter((f) => (f.name ?? '').toLowerCase().includes(needle))
      : list;

  const actions = [
    {
      title: t('pro-studio/actions:section1.title', {
        defaultValue: 'Configuration',
      }),
      label: t('pro-studio/actions:section1.action', {
        defaultValue: 'Upload Config',
      }),
      columns: configurationsColumns,
      data: byName(configurations),
      type: 'configFile',
      accept: '.py',
    },
    {
      title: t('pro-studio/actions:section2.title', {
        defaultValue: 'Functions',
      }),
      label: t('pro-studio/actions:section2.action', {
        defaultValue: 'Upload Scripts',
      }),
      columns: functionsColumns,
      data: byName(functions),
      type: 'scriptFile',
      accept: '.py,.csv,.json,.pem,.crt,.md',
    },
  ];

  return (
    <>
      {!embedded && (
        <Stack gutter="xsmall" alignItems="center">
          <TextTitle test-id="ps-actions-page-title" size="small">
            <TranslationComponent i18nKey="pro-studio/actions:title">
              Actions
            </TranslationComponent>
          </TextTitle>
          <Tooltip test-id="ps-actions-page-info-tooltip" placement="right">
            <Icon
              test-id="ps-actions-page-info-icon"
              name="info"
              color="gray"
              size="small"
            />
            <TextCaption variant="primary" slot="content">
              <TranslationComponent i18nKey="pro-studio/actions:subtitle.part1">
                Actions allow the designer to perform custom integrations and
                special logic. Actions must have corresponding script(s), utils,
                and config files uploaded below. Config files must have a .py
                extension. For more information regarding Actions, please read
              </TranslationComponent>{' '}
              <a href="#">
                <TranslationComponent i18nKey="pro-studio/actions:subtitle.part2">
                  the documentation
                </TranslationComponent>
              </a>
              .
            </TextCaption>
          </Tooltip>
        </Stack>
      )}
      {!embedded && <Spacer size="large" />}
      {(embedded
        ? [
            {
              ...actions[1],
              title: `Custom · ${byName(functions).length}`,
              hideUpload: true,
            },
            {
              ...actions[0],
              title: `Config files · ${byName(configurations).length}`,
              hideUpload: false,
            },
          ]
        : actions
      ).map((action, i) => (
        <Fragment key={action.type}>
          <ActionSection
            {...action}
            compact={embedded}
            onRowSelection={handleOpenEditor}
            viewOnly={isViewer}
          />
          {embedded && i === 0 && betweenSections}
        </Fragment>
      ))}
      {editorModal}
    </>
  );
};

/** The Actions page body, for Build's Actions panel. */
export const ActionsContent = ActionsPage;

// With the 5-stage nav on, Actions lives in Build, in the panel the Build
// bar's Actions button opens (Bobcat status check, 2026-09-29); its old
// route lands there with the panel open.
export const Actions = withStageNavRedirect(WithLoadingState(ActionsPage), {
  stage: 'build',
  openActions: true,
});
